import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ArrowLeft, FolderLock, MessageCircleQuestion, FileText, Lock } from "lucide-react";
import { AskQuestionBox, QuestionItem } from "../components/dataroom/QuestionParts";
import { DocumentGuide, UploadBox, DocumentRow } from "../components/dataroom/DocumentParts";
import { t } from "../lib/i18n";

const SectionNav = ({ sections, counts, active, onPick, testPrefix }) => (
  <nav className="hairline bg-card" data-testid={`${testPrefix}-sections`}>
    <button type="button" onClick={() => onPick("all")}
      className={`w-full text-left px-5 py-3 flex justify-between text-sm transition-colors ${active === "all" ? "bg-[#1E3A5F] text-bone" : "hover:bg-bone"}`}
      data-testid={`${testPrefix}-section-all`}>
      <span>{t("Todas las secciones")}</span>
      <span className="font-mono-label opacity-70">{Object.values(counts).reduce((a, b) => a + b, 0)}</span>
    </button>
    {Object.entries(sections).map(([k, label]) => (
      <button key={k} type="button" onClick={() => onPick(k)}
        className={`w-full text-left px-5 py-3 flex justify-between text-sm hairline-t transition-colors ${active === k ? "bg-[#1E3A5F] text-bone" : "hover:bg-bone"}`}
        data-testid={`${testPrefix}-section-${k}`}>
        <span>{label}</span>
        <span className="font-mono-label opacity-70">{counts[k] || 0}</span>
      </button>
    ))}
  </nav>
);

const groupBy = (items, filter) => items.filter((i) => filter === "all" || i.section === filter);
const countBy = (items) => items.reduce((acc, i) => ({ ...acc, [i.section]: (acc[i.section] || 0) + 1 }), {});

export default function DataRoom() {
  const { companyId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [qFilter, setQFilter] = useState("all");
  const [dFilter, setDFilter] = useState("all");
  const [uploadSection, setUploadSection] = useState("financiero");
  const guideKey = `relevo_dataroom_guide_${companyId}`;
  const [guideOpen, setGuideOpen] = useState(() => !localStorage.getItem(guideKey));

  const load = useCallback(() => {
    api.get(`/companies/${companyId}/dataroom`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "No se pudo abrir el Data Room"));
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const dismissGuide = () => { localStorage.setItem(guideKey, "1"); setGuideOpen(false); };
  const backHref = user?.role === "seller" ? `/seller/company/${companyId}` : user?.role === "investor" ? `/investor/opportunity/${companyId}` : `/searcher/company/${companyId}`;

  if (error) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="dataroom-locked">
          <Lock className="w-8 h-8 mx-auto text-muted-foreground mb-4" />
          <h1 className="font-serif-display text-3xl mb-2">{t("Data Room no disponible")}</h1>
          <p className="text-muted-foreground">{error}</p>
          <Link to={backHref} className="font-mono-label text-gold mt-6 inline-flex items-center gap-2"><ArrowLeft className="w-3 h-3" />{" "}{t("Volver")}</Link>
        </div>
      </Layout>
    );
  }
  if (!data) return <Layout><div className="px-10 py-20 font-mono-label text-muted-foreground animate-pulse">{t("Abriendo Data Room…")}</div></Layout>;

  const { sections, questions, my_pending_questions: pending, documents, is_owner: isOwner, guide, company } = data;
  const qCounts = countBy([...questions, ...pending]);
  const dCounts = countBy(documents);
  const visibleQ = groupBy(questions, qFilter);
  const visiblePending = groupBy(pending, qFilter);
  const visibleD = groupBy(documents, dFilter);
  const openCount = isOwner ? questions.filter((q) => q.status !== "answered").length : 0;

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="dataroom-page">
        <Link to={backHref} className="font-mono-label text-muted-foreground inline-flex items-center gap-2 hover:text-foreground mb-6" data-testid="dataroom-back">
          <ArrowLeft className="w-3 h-3" />{" "}{t("Volver a la empresa")}
        </Link>

        <div className="hairline bg-[#1E3A5F] text-bone p-8 md:p-10 flex flex-wrap items-end justify-between gap-6" data-testid="dataroom-hero">
          <div>
            <span className="font-mono-label text-gold flex items-center gap-2"><FolderLock className="w-3 h-3" />{" "}{t("Data Room · Pre-Due Diligence")}</span>
            <h1 className="font-serif-display text-3xl md:text-4xl mt-2">{isOwner ? company.name : `${company.name} · ${company.project_code}`}</h1>
            <p className="text-bone/75 mt-2 max-w-2xl text-sm">
              {isOwner
                ? t("Clasifica y responde las preguntas de los compradores y publica la documentación de la fase de análisis. Todo lo publicado es visible para los compradores con NDA aprobado, sin revelar quién preguntó.")
                : t("Preguntas respondidas y documentación compartida por el vendedor con todos los compradores que han firmado el NDA. Las preguntas se muestran de forma anónima.")}
            </p>
          </div>
          <div className="flex gap-6">
            <Stat label={t("Preguntas")} value={questions.length} />
            <Stat label={t("Documentos")} value={documents.length} />
            {isOwner && <Stat label={t("Pendientes")} value={openCount} accent />}
          </div>
        </div>

        <Tabs defaultValue="qa" className="mt-8">
          <TabsList className="rounded-none h-12 bg-card hairline p-1 gap-1">
            <TabsTrigger value="qa" className="rounded-none h-10 px-5 data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-bone" data-testid="dataroom-tab-qa">
              <MessageCircleQuestion className="w-3.5 h-3.5 mr-2" />{" "}{t("Preguntas y Respuestas")}
            </TabsTrigger>
            <TabsTrigger value="docs" className="rounded-none h-10 px-5 data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-bone" data-testid="dataroom-tab-docs">
              <FileText className="w-3.5 h-3.5 mr-2" />{" "}{t("Documentos")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="qa" className="mt-6">
            <div className="grid lg:grid-cols-[260px_1fr] gap-6 items-start">
              <SectionNav sections={sections} counts={qCounts} active={qFilter} onPick={setQFilter} testPrefix="qa" />
              <div className="space-y-5">
                {!isOwner && <AskQuestionBox companyId={companyId} sections={sections} onAsked={load} />}
                {!isOwner && visiblePending.length > 0 && (
                  <div className="hairline bg-card" data-testid="dataroom-my-pending">
                    <div className="px-6 py-3 hairline-b font-mono-label text-[#E0712C]">{t("Tus preguntas pendientes de respuesta")}</div>
                    {visiblePending.map((q, i) => (
                      <div key={q.question_id} className={i > 0 ? "hairline-t" : ""}>
                        {qFilter === "all" && <div className="px-6 pt-4 font-mono-label text-muted-foreground" style={{ fontSize: "0.58rem" }}>{sections[q.section]}</div>}
                        <QuestionItem q={q} isOwner={false} mine sections={sections} onChanged={load} />
                      </div>
                    ))}
                  </div>
                )}
                {visibleQ.length === 0 ? (
                  <div className="hairline bg-card p-12 text-center" data-testid="dataroom-qa-empty">
                    <p className="font-serif-display text-xl mb-1">{t("Sin preguntas en esta sección")}</p>
                    <p className="text-sm text-muted-foreground">{isOwner ? t("Cuando un comprador pregunte, aparecerá aquí para que la clasifiques y respondas.") : t("Las preguntas respondidas por el vendedor aparecerán aquí.")}</p>
                  </div>
                ) : (
                  <div className="hairline bg-card" data-testid="dataroom-qa-list">
                    {visibleQ.map((q, i) => (
                      <div key={q.question_id} className={i > 0 ? "hairline-t" : ""}>
                        {qFilter === "all" && <div className="px-6 pt-4 font-mono-label text-muted-foreground" style={{ fontSize: "0.58rem" }}>{sections[q.section]}</div>}
                        <QuestionItem q={q} isOwner={isOwner} sections={sections} onChanged={load} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="docs" className="mt-6">
            <div className="grid lg:grid-cols-[260px_1fr] gap-6 items-start">
              <SectionNav sections={sections} counts={dCounts} active={dFilter} onPick={setDFilter} testPrefix="docs" />
              <div className="space-y-5">
                {isOwner && guideOpen && (
                  <DocumentGuide guide={guide} sections={sections} onDismiss={dismissGuide} onPick={(s) => { setUploadSection(s); setDFilter(s); }} />
                )}
                {isOwner && !guideOpen && (
                  <button type="button" onClick={() => setGuideOpen(true)} className="font-mono-label text-gold hover:opacity-70" data-testid="dataroom-guide-reopen">
                    {t("Ver guía de documentos habituales")}
                  </button>
                )}
                {isOwner && <UploadBox companyId={companyId} sections={sections} section={uploadSection} setSection={setUploadSection} onUploaded={load} />}
                {visibleD.length === 0 ? (
                  <div className="hairline bg-card p-12 text-center" data-testid="dataroom-docs-empty">
                    <p className="font-serif-display text-xl mb-1">{t("Sin documentos en esta sección")}</p>
                    <p className="text-sm text-muted-foreground">{isOwner ? t("Publica aquí la documentación que quieras compartir con los compradores.") : t("El vendedor aún no ha publicado documentación en esta sección.")}</p>
                  </div>
                ) : (
                  <div className="hairline bg-card" data-testid="dataroom-docs-list">
                    {visibleD.map((d, i) => (
                      <div key={d.document_id} className={i > 0 ? "hairline-t" : ""}>
                        {dFilter === "all" && <div className="px-6 pt-3 font-mono-label text-muted-foreground" style={{ fontSize: "0.58rem" }}>{sections[d.section]}</div>}
                        <DocumentRow d={d} isOwner={isOwner} onDeleted={load} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="text-right">
    <div className={`font-serif-display text-3xl ${accent ? "text-[#E4B54B]" : ""}`}>{value}</div>
    <div className="font-mono-label text-bone/60" style={{ fontSize: "0.58rem" }}>{label}</div>
  </div>
);
